/**
 * ReviewNudge.jsx — Phase 160. Persistent Home-dashboard reminder for
 * founders/admins listing active staff still missing a performance review
 * this month. Appears from the 25th IST and stays until "Mark done" is
 * clicked (dismissed per-user, per-month on the server).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import usePermissions from "@/lib/usePermissions";
import { ClipboardCheck, Check, ArrowRight } from "lucide-react";

export const ReviewNudge = () => {
  const can = usePermissions();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!can("performance_reviews")) return;
    api.get("/performance-reviews/missing-nudge").then((r) => setData(r.data)).catch(() => {});
  }, [can]);

  const markDone = async () => {
    setDismissing(true);
    try {
      await api.post("/performance-reviews/missing-nudge/dismiss");
      setData((d) => ({ ...d, active: false, dismissed: true }));
    } catch { /* ignore */ }
    finally { setDismissing(false); }
  };

  if (!data?.active) return null;

  const preview = data.missing.slice(0, 8);
  const extra = data.missing_count - preview.length;

  return (
    <div className="mb-4 card-flat border-l-4 border-l-[#F54800] p-4" data-testid="review-nudge">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2.5 min-w-0">
          <ClipboardCheck size={18} className="text-[#F54800] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="font-display font-semibold text-slate-900 text-sm" data-testid="review-nudge-title">
              {data.missing_count} team member{data.missing_count !== 1 ? "s" : ""} still need a review this month
            </div>
            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-1.5" data-testid="review-nudge-list">
              {preview.map((m) => (
                <span key={m.id} className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                  {m.name}{m.business_unit ? ` · ${m.business_unit}` : ""}
                </span>
              ))}
              {extra > 0 && <span className="inline-flex items-center px-1.5 py-0.5 text-slate-400">+{extra} more</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => nav("/my-hr?tab=performance")}
            className="inline-flex items-center gap-1 text-xs font-medium text-[#F54800] hover:bg-[#FFF4EE] px-2.5 py-1.5 rounded-sm"
            data-testid="review-nudge-review-now"
          >
            Review now <ArrowRight size={13} />
          </button>
          <button
            onClick={markDone}
            disabled={dismissing}
            className="inline-flex items-center gap-1 text-xs font-medium text-white bg-slate-900 hover:bg-slate-700 px-2.5 py-1.5 rounded-sm disabled:opacity-50"
            data-testid="review-nudge-mark-done"
          >
            <Check size={13} /> {dismissing ? "Saving…" : "Mark done"}
          </button>
        </div>
      </div>
    </div>
  );
};
